/**
 * Core API Client Configuration
 * 
 * Centralized axios instance with interceptors for authentication,
 * CSRF protection, and error handling.
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { getApiUrl } from '../../utils/urls'
import { getCSRFToken } from '../../utils/cookies'
import { debugLog, debugWarn } from '../../utils/debugLog'

// Timeout configuration for different operation types
const TIMEOUT_CONFIG = {
  default: 30000,          // 30 seconds for most operations
  dataManagement: 300000,  // 5 minutes for data clear/bulk operations
  largeSync: 600000,       // 10 minutes for large sync operations
}

/**
 * Create and configure the main axios instance
 */
const createApiClient = (): AxiosInstance => {
  // Use relative API path - nginx handles routing to backend
  const baseURL = getApiUrl()

  const client = axios.create({
    baseURL: baseURL,
    timeout: TIMEOUT_CONFIG.default,
    withCredentials: true, // Include cookies with all requests
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor for logging, cache control, and CSRF protection
  client.interceptors.request.use(
    (config) => {
      debugLog(`🔍 API Request: ${config.method?.toUpperCase()} ${config.url}`)

      // Apply endpoint-specific timeouts
      if (config.url?.includes('/sync/clear-data') ||
          config.url?.includes('/sync/bulk-') ||
          config.url?.includes('/data-management/')) {
        config.timeout = TIMEOUT_CONFIG.dataManagement
        debugLog(`⏱️ Extended timeout applied: ${config.timeout / 1000}s`)
      } else if (config.url?.includes('/sync/trigger') &&
                 config.method?.toLowerCase() === 'post') {
        config.timeout = TIMEOUT_CONFIG.largeSync
        debugLog(`⏱️ Large sync timeout applied: ${config.timeout / 1000}s`)
      }

      // Add CSRF token for state-changing requests
      const method = config.method?.toUpperCase()
      if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        const csrfToken = getCSRFToken()
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken
          debugLog('🔒 Added CSRF token to request')
        } else {
          debugWarn('⚠️ No CSRF token found for state-changing request')
        }
      }
      
      // Add no-cache headers for sync-related endpoints to prevent stale data
      if (config.url?.includes('/sync/operations/active') || 
          config.url?.includes('/sync/') && config.method?.toLowerCase() === 'get') {
        config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
        config.headers['Pragma'] = 'no-cache'
        config.headers['Expires'] = '0'
      }
      
      return config
    },
    (error) => {
      console.error('❌ API Request Error:', error.message || 'Unknown error')
      return Promise.reject(error)
    }
  )

  // Response interceptor for logging and error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      debugLog(`✅ API Response: ${response.status} ${response.config.url}`)
      return response
    },
    async (error) => {
      debugWarn('❌ API Response Error:', error.message || 'Unknown error')
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        // Don't log authentication warnings for public invitation endpoints
        const isInvitationEndpoint = error.config?.url?.includes('/invitations/')
        if (!isInvitationEndpoint) {
          debugWarn('⚠️ Authentication required (401)')
          // Note: AdminLayout and InspectorLayout handle authentication redirects
          // We don't redirect here to avoid conflicts and double redirects
        }
      } else if (error.response?.status === 403) {
        const errorDetail = error.response?.data?.detail?.toLowerCase() || ''
        
        // Check if this is a CSRF token error
        if (errorDetail.includes('csrf') && !error.config._csrfRetry) {
          debugWarn('🔄 CSRF token error - attempting to refresh and retry')
          
          try {
            // Make a GET request to refresh the CSRF token
            await client.get('/health')
            
            // Get the fresh CSRF token
            const freshToken = getCSRFToken()
            if (freshToken) {
              // Mark this request as a retry to prevent infinite loops
              error.config._csrfRetry = true
              error.config.headers['X-CSRF-Token'] = freshToken
              debugLog('🔒 Retrying with fresh CSRF token')
              
              // Retry the original request
              return client.request(error.config)
            }
          } catch (refreshError: any) {
            console.error('❌ Failed to refresh CSRF token:', refreshError?.message || 'Unknown error')
          }
        }
        
        debugWarn('⚠️ Forbidden (403):', error.response?.data?.detail || 'Access denied')
      } else if (error.response?.status === 404) {
        debugWarn('⚠️ Not found (404):', error.config?.url)
      } else if (error.response?.status === 500) {
        console.error('💥 Server error (500):', error.response?.data?.detail || 'Internal server error')
      }
      
      return Promise.reject(error)
    }
  )

  return client
}

// Create and export the singleton API client instance
export const apiClient = createApiClient()

// Export the type for use in other modules
export type ApiClient = typeof apiClient